import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { getToken, getSession, setSession, clearSession } from "@/lib/sessionStore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (token) {
      const s = getSession();
      if (s?.user) setUser(s.user);
      if (s?.tenant) setTenant(s.tenant);
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setSession({ token: data.token, user: data.user, tenant: data.tenant });
    setUser(data.user);
    setTenant(data.tenant);
    return data;
  };

  const register = async (payload) => {
    // After Stripe rollout (Feb/2026) register no longer emits a JWT.
    // It returns CheckoutIntent ({registration_token, tenant_id, email})
    // and the caller MUST drive the user through /checkout next.
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const completeAutoLogin = (token, userObj, tenantObj) => {
    /* Used by the checkout success page after the webhook activates
       the tenant — drops the user straight into the app without
       asking for the password again. */
    setSession({ token, user: userObj, tenant: tenantObj });
    setUser(userObj);
    setTenant(tenantObj);
  };

  const logout = () => {
    clearSession();
    setUser(null);
    setTenant(null);
  };

  const refresh = async () => {
    // Re-fetch just the user from /auth/me. Tenant updates come via
    // direct calls to setTenant() with the PATCH response payload —
    // tenants don't have a dedicated tenant-scoped read endpoint.
    try {
      const me = await api.get("/auth/me");
      const token = getToken();
      setSession({ token, user: me.data, tenant });
      setUser(me.data);
    } catch {
      /* ignored — session likely expired */
    }
  };

  return (
    <AuthContext.Provider value={{ user, tenant, loading, login, register, logout, refresh, setTenant, setUser, completeAutoLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
